import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Search, X, Users, ChevronRight } from 'lucide-react'
import { TrustGauge } from '../trust-gauge/TrustGauge.tsx'
import type { SearchResultItem, SearchResultCategory } from './mock-data'
import styles from './SearchOverlay.module.css'

export interface SearchOverlayProps {
  open: boolean
  onClose: () => void
  query: string
  onQueryChange: (query: string) => void
  categories: SearchResultCategory[]
  onResultClick?: (item: SearchResultItem) => void
  placeholder?: string
  loading?: boolean
}

function TypeBadge({ type, isGuest }: { type: SearchResultItem['type']; isGuest?: boolean }) {
  const labels: Record<string, string> = {
    organization: 'Club',
    league: 'League',
    university: 'University',
    team: 'Team',
    player: 'Player',
  }
  const variants: Record<string, string> = {
    organization: styles.badgeDefault,
    league: styles.badgeInfo,
    university: styles.badgeSuccess,
    team: styles.badgeDefault,
    player: styles.badgeDefault,
  }
  // "Guest" says more than "Player" here — the category heading already says
  // these are players, and whether this one is a real account is the thing a
  // searcher needs to know before clicking.
  if (isGuest) {
    return <span className={`${styles.typeBadge} ${styles.badgeInfo}`}>Guest</span>
  }
  return (
    <span className={`${styles.typeBadge} ${variants[type]}`}>
      {labels[type]}
    </span>
  )
}

function ResultItem({
  item,
  onClick,
}: {
  item: SearchResultItem
  onClick?: (item: SearchResultItem) => void
}) {
  return (
    <button
      className={styles.resultItem}
      onClick={() => onClick?.(item)}
      type="button"
    >
      <div
        className={styles.resultLogo}
        style={!item.logoUrl && item.brandColor ? { background: item.brandColor, borderColor: 'transparent' } : undefined}
      >
        {item.logoUrl ? (
          <img src={item.logoUrl} alt="" className={styles.resultLogoImg} />
        ) : (
          <span
            className={item.brandColor ? styles.resultLogoBrandedFallback : styles.resultLogoFallback}
          >
            {item.name.charAt(0)}
          </span>
        )}
      </div>
      <div className={styles.resultInfo}>
        <span className={styles.resultName}>{item.name}</span>
        <span className={styles.resultDesc}>{item.description}</span>
      </div>
      <div className={styles.resultMeta}>
        {item.trustScore != null && !item.isGuest && (
          <TrustGauge score={item.trustScore} size={36} />
        )}
        {item.memberCount != null && (
          <span className={styles.memberCount}>
            <Users size={11} style={{ marginRight: 3, verticalAlign: -1 }} />
            {item.memberCount}
          </span>
        )}
        <TypeBadge type={item.type} isGuest={item.isGuest} />
        <ChevronRight size={14} style={{ color: 'var(--nexus-text-muted)', opacity: 0.5 }} />
      </div>
    </button>
  )
}

function CategorySection({
  category,
  onResultClick,
}: {
  category: SearchResultCategory
  onResultClick?: (item: SearchResultItem) => void
}) {
  if (category.results.length === 0 && !category.comingSoon) return null

  let content: ReactNode
  if (category.comingSoon) {
    content = (
      <div className={styles.comingSoon}>Coming soon</div>
    )
  } else {
    content = category.results.map((item) => (
      <ResultItem key={item.id} item={item} onClick={onResultClick} />
    ))
  }

  return (
    <div className={styles.category}>
      <div className={styles.categoryHeader}>{category.label}</div>
      {content}
    </div>
  )
}

export function SearchOverlay({
  open,
  onClose,
  query,
  onQueryChange,
  categories,
  onResultClick,
  placeholder = 'Search organizations, teams, players...',
}: SearchOverlayProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const orig = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = orig
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  const hasQuery = query.trim().length > 0
  const hasResults = categories.some((c) => c.results.length > 0)

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.container} onClick={(e) => e.stopPropagation()}>
        <div className={styles.inputWrapper}>
          <Search size={20} className={styles.inputIcon} />
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            placeholder={placeholder}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            className={styles.escBtn}
            onClick={onClose}
            aria-label="Close search"
          >
            <X size={16} />
            <kbd className={styles.escKbd}>ESC</kbd>
          </button>
        </div>

        <div className={styles.results}>
          {hasQuery && !hasResults && (
            <div className={styles.empty}>
              No results for &ldquo;{query}&rdquo;
            </div>
          )}
          {!hasQuery && hasResults && (
            <div className={styles.categoryHeader} style={{ padding: 'var(--nexus-space-sm) var(--nexus-space-md)' }}>
              Recommended
            </div>
          )}
          {categories.map((cat) => (
            <CategorySection
              key={cat.label}
              category={cat}
              onResultClick={(item) => {
                onResultClick?.(item)
                onClose()
              }}
            />
          ))}
          {!hasQuery && !hasResults && (
            <div className={styles.hint}>
              <Search size={24} className={styles.hintIcon} />
              Search for organizations, teams, and players across Carousel
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
